/**
 * Admin API - User management and approval endpoints (User Story 4)
 */

import { fetcher } from '../utils/fetcher';
import { User } from '../types/user';

const ADMIN_URL = '/api/v1/users';

/**
 * Get all users with pending status awaiting approval.
 * Requires admin privileges.
 */
export const getPendingUsers = async (): Promise<User[]> => {
  return await fetcher(`${ADMIN_URL}/pending`);
};

/**
 * Approve a pending user registration.
 * Changes status from 'pending' to 'active' and sends approval email.
 * Requires admin privileges.
 */
export const approveUser = async (userId: string): Promise<User> => {
  return await fetcher(`${ADMIN_URL}/approve/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });
};

/**
 * Reject a pending user registration with reason.
 * Changes status to 'rejected' and sends rejection email.
 * Requires admin privileges.
 */
export const rejectUser = async (userId: string, reason: string): Promise<User> => {
  return await fetcher(`${ADMIN_URL}/reject/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason })
  });
};

/**
 * Deactivate a user account.
 * Changes status to 'deactivated', preventing login while preserving data.
 * Requires admin privileges.
 */
export const deactivateUser = async (userId: string): Promise<User> => {
  return await fetcher(`${ADMIN_URL}/deactivate/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' }
  });
};

/**
 * T129: Initiate password reset for a user (admin only)
 * 
 * Creates a time-limited reset token (24 hours), stores it in the database,
 * and sends an email to the user with a reset link.
 * 
 * @param userId - The UUID of the user whose password should be reset
 * @returns Response with token, expiry, and success message
 */
export interface PasswordResetInitiateResponse {
  message: string;
  token: string;
  user_id: string;
  email: string;
  expires_at: string;
}

export const initiatePasswordReset = async (userId: string): Promise<PasswordResetInitiateResponse> => {
  return await fetcher(`${ADMIN_URL}/password-reset/initiate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId })
  });
};

/**
 * Get all users (admin only).
 * Returns list of all users with their status, role, and profile information.
 */
export const getAllUsers = async (): Promise<User[]> => {
  return await fetcher(`${ADMIN_URL}/read`);
};
